import React, { useState, useEffect, useRef } from 'react';
import { Search, Package } from 'lucide-react';
import Modal from '../ui/Modal';
import Input from '../forms/Input';
import apiService from '../../services/apiService';
import { formatMoney } from '../../utils/formatters';
import './ProductsCatalogModal.css';

const ProductsCatalogModal = ({
    isOpen,
    onClose,
    onSelectProduct,
    getPrice,
    title = "Todos los Productos"
}) => {
    const [productos, setProductos] = useState([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const containerRef = useRef(null);
    const rowRefs = useRef({});
    const searchDebounceRef = useRef(null);

    // Cargar productos iniciales cuando se abre el modal
    useEffect(() => {
        if (isOpen) {
            setProductos([]);
            setPage(1);
            setSearch('');
            setHasMore(true);
            setSelectedIndex(0);
            loadProducts(1, '');
        }
    }, [isOpen]);

    // Cargar productos
    const loadProducts = async (pageNum, searchTerm) => {
        try {
            setLoading(pageNum === 1);
            const result = await apiService.getProductosPaginados({
                pagina: pageNum,
                limite: 50,
                busqueda: searchTerm
            });
            if (result.success && result.data) {
                if (pageNum === 1) {
                    setProductos(result.data.productos || []);
                } else {
                    setProductos(prev => [...prev, ...(result.data.productos || [])]);
                }
                setTotal(result.data.paginacion?.total || 0);
                setHasMore(result.data.paginacion?.hay_mas || false);
                setPage(pageNum);
            }
        } catch (error) {
            console.error('Error cargando productos:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Cargar más productos
    const loadMore = async () => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        await loadProducts(page + 1, search);
    };

    // Manejar scroll
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loadingMore) {
            loadMore();
        }
    };

    // Manejar cambio de búsqueda con debounce
    const handleSearchChange = (newSearch) => {
        setSearch(newSearch);

        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        searchDebounceRef.current = setTimeout(() => {
            setSelectedIndex(0);
            loadProducts(1, newSearch);
        }, 300);
    };

    // Navegación por teclado
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e) => {
            if (productos.length === 0) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex(prev => Math.min(prev + 1, productos.length - 1));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex(prev => Math.max(prev - 1, 0));
            } else if (e.key === 'Enter') {
                e.preventDefault();
                const selected = productos[selectedIndex];
                if (selected) {
                    handleSelect(selected);
                }
            } else if (e.key === 'Escape') {
                handleClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, productos, selectedIndex]);

    // Auto-scroll a la fila seleccionada
    useEffect(() => {
        if (isOpen && rowRefs.current[selectedIndex]) {
            rowRefs.current[selectedIndex].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
            });
        }
    }, [selectedIndex, isOpen]);

    const handleSelect = (producto) => {
        onSelectProduct(producto);
        handleClose();
    };

    const handleClose = () => {
        setSearch('');
        setSelectedIndex(0);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={title}
            size="xlarge"
        >
            <div className="products-catalog-modal">
                {/* Búsqueda */}
                <div className="pcm-search">
                    <Input
                        placeholder="Buscar por nombre o código..."
                        value={search}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        icon={<Search size={20} />}
                        autoFocus
                    />
                </div>

                {/* Tabla de productos */}
                <div
                    ref={containerRef}
                    className="pcm-table-container"
                    onScroll={handleScroll}
                >
                    {loading && productos.length === 0 ? (
                        <div className="pcm-loading">
                            <div className="pcm-spinner" />
                            <p>Cargando productos...</p>
                        </div>
                    ) : productos.length === 0 ? (
                        <div className="pcm-empty">
                            <Package size={48} />
                            <p>No se encontraron productos</p>
                        </div>
                    ) : (
                        <>
                            <table className="pcm-table">
                                <thead>
                                    <tr>
                                        <th className="col-codigo">Código</th>
                                        <th className="col-nombre">Nombre</th>
                                        <th className="col-unidad">Unidad</th>
                                        <th className="col-precio">Precio</th>
                                        <th className="col-stock">Stock</th>
                                        <th className="col-accion">Acción</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {productos.map((producto, index) => (
                                        <tr
                                            key={producto.id}
                                            ref={(el) => rowRefs.current[index] = el}
                                            className={selectedIndex === index ? 'selected' : ''}
                                            onClick={() => handleSelect(producto)}
                                            onMouseEnter={() => setSelectedIndex(index)}
                                        >
                                            <td className="col-codigo">
                                                {producto.codigo_barras || producto.id}
                                            </td>
                                            <td className="col-nombre">
                                                {producto.nombre}
                                            </td>
                                            <td className="col-unidad">
                                                {producto.unidad_medida || 'pz'}
                                            </td>
                                            <td className="col-precio">
                                                {formatMoney(getPrice ? getPrice(producto) : producto.precio1)}
                                            </td>
                                            <td className={`col-stock ${producto.stock > 0 ? 'in-stock' : 'no-stock'}`}>
                                                {producto.stock || 0}
                                            </td>
                                            <td className="col-accion">
                                                <button
                                                    className="pcm-add-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSelect(producto);
                                                    }}
                                                >
                                                    + Agregar
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Loading más productos */}
                            {loadingMore && (
                                <div className="pcm-loading-more">
                                    <div className="pcm-spinner-small" />
                                </div>
                            )}

                            {/* Fin de lista */}
                            {!hasMore && productos.length > 0 && (
                                <div className="pcm-end">— Fin de la lista —</div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="pcm-footer">
                    <span>Mostrando {productos.length} de {total} productos</span>
                    {hasMore && <span className="pcm-hint">Desplázate para cargar más</span>}
                </div>

                {/* Atajos de teclado */}
                <div className="pcm-shortcuts">
                    <kbd>↑↓</kbd> Navegar | <kbd>Enter</kbd> Agregar | <kbd>Esc</kbd> Cerrar
                </div>
            </div>
        </Modal>
    );
};

export default ProductsCatalogModal;
